// src/systems/relics.ts
//
// Phase 8 — relic engine. Pure data-driven hooks for the 5 active-effect relics
// (lucky_charm, house_edge, cold_streak, hot_streak, safety_net). Two passive
// relics (croupiers_smile, peek) are surfaced via getRequiredFiches/getPeekCount
// helpers; compound_interest is wired in Phase 7's RoundScene.handleRoundClear.
//
// Hooks mutate a SpinContext / ZeroContext per relic; the runner clones the
// base context, applies each handler in relics order, and returns the result.

import type { Relic, Ball } from '../state/types';

/** Per-spin context passed to relic hooks. */
export interface SpinContext {
  // Read
  ball: Ball | null;
  isFirstSpinOfRound: boolean;
  consecutiveRedHits: number; // tracked across spins within a round
  consecutiveBlackHits: number;
  // Mutable bonuses (relics may add to these)
  flatMultBonus: number; // applied after fiche.mult
  payoutMultiplier: number; // multiplied on payout (e.g. House Edge → 1.5)
  // Outcomes
  spinMoneyDelta: number; // negative on House Edge ($-1 per spin)
  /**
   * Phase 13 (RELIC-09): count of void positions on the wheel for THIS run.
   * Read by void_walker.onSpinStart. Optional so v1 test fixtures that
   * construct a SpinContext literal without this field continue to typecheck.
   * RoundScene.onSpinTapped passes `s.voidPositions.length` when building
   * the base context.
   */
  voidPositionsCount?: number;
}

/** Per-zero context: the relic may convert a 0 result into a re-roll. */
export interface ZeroContext {
  isFirstZeroOfRound: boolean;
  shouldReroll: boolean;
}

/** Per-round-clear context (placeholder; currently unused). */
export interface RoundClearContext {
  roundIndex: 0 | 1 | 2;
}

/** Relic hook handlers — each is a small function that mutates context. */
export interface RelicHandlers {
  onSpinStart?(ctx: SpinContext, relic: Relic): void;
  onZero?(ctx: ZeroContext, relic: Relic): void;
  onRoundClear?(ctx: RoundClearContext, relic: Relic): void;
}

export const RELIC_HANDLERS: Record<string, RelicHandlers> = {
  lucky_charm: {
    onSpinStart(ctx) {
      if (ctx.isFirstSpinOfRound) ctx.flatMultBonus += 5;
    },
  },
  house_edge: {
    onSpinStart(ctx) {
      ctx.payoutMultiplier *= 1.5;
      ctx.spinMoneyDelta -= 1;
    },
  },
  cold_streak: {
    onSpinStart(ctx) {
      ctx.flatMultBonus += ctx.consecutiveBlackHits;
    },
  },
  hot_streak: {
    onSpinStart(ctx) {
      ctx.flatMultBonus += ctx.consecutiveRedHits;
    },
  },
  safety_net: {
    /**
     * Phase 13 (BETS-09): safety_net does NOT trigger on void slots.
     * The caller (RoundScene.onSpinTapped, plan 13-04) is responsible
     * for ONLY invoking runOnZero when the resolved slot is
     * `slot.kind === 'number' && slot.value === 0`. Void slots are
     * full losses (isLosingSlot returns true) and never reach this
     * handler — invoking safety_net on void would be a refund-every-spin
     * exploit (T-13-08).
     */
    onZero(ctx) {
      if (ctx.isFirstZeroOfRound) ctx.shouldReroll = true;
    },
  },
  // Phase 13 (RELIC-09): void_walker — +1 flatMultBonus per void slot on the wheel,
  // applied to every fiche on every spin. No first-spin gate, no streak gate.
  // Defensive null-coalesce: if RoundScene omits voidPositionsCount (e.g. old saves
  // or tests that don't set it), treat as 0 so the relic is a no-op, not a crash.
  void_walker: {
    onSpinStart(ctx) {
      ctx.flatMultBonus += ctx.voidPositionsCount ?? 0;
    },
  },
  // croupiers_smile, peek, compound_interest are not scoring relics — they're
  // surfaced via getRequiredFiches/getPeekCount or by Phase 7 (interest cap).
};

/** Run all spin-start hooks and return the accumulated context. */
export function runSpinStart(relics: Relic[], baseCtx: SpinContext): SpinContext {
  const ctx: SpinContext = { ...baseCtx };
  relics.forEach((r) => {
    RELIC_HANDLERS[r.defId]?.onSpinStart?.(ctx, r);
  });
  return ctx;
}

/** Run zero-handlers; returns whether to reroll. */
export function runOnZero(relics: Relic[], baseCtx: ZeroContext): ZeroContext {
  const ctx: ZeroContext = { ...baseCtx };
  relics.forEach((r) => {
    RELIC_HANDLERS[r.defId]?.onZero?.(ctx, r);
  });
  return ctx;
}

/** Croupier's Smile → place 4 fiches per spin instead of 3. */
export function getRequiredFiches(relics: Relic[]): number {
  return relics.some((r) => r.defId === 'croupiers_smile') ? 4 : 3;
}

/** Peek → see next 2 balls instead of 1. */
export function getPeekCount(relics: Relic[]): number {
  return relics.some((r) => r.defId === 'peek') ? 2 : 1;
}
